import { useState, useRef, useEffect, useMemo } from "react";
import { usePosicionDesplegable } from "../posicionDesplegable";
import Icon from "../Icon";
import { Avatar } from "../Atoms";

// Cuantas opciones hay que tener para que salga el buscador. Por debajo de esto
// la lista se lee entera de un vistazo y el campo de texto solo estorba; por
// encima —la plantilla en los filtros de personas— desplazarse a ciegas es la
// unica forma de encontrar a alguien.
const MIN_BUSCADOR = 8;

// Se normaliza sin acentos ni mayusculas: quien busca a Alvaro casi nunca
// escribe "Álvaro", y la lista tiene acentos en la mitad de los nombres.
const norml = s => String(s ?? "").toLowerCase()
  .normalize("NFD").replace(/[̀-ͯ]/g, "");

// Desplegable de varias opciones. Es el UNICO control de filtro de la
// aplicacion: antes el gestor usaba <select> de una opcion y Notion estas
// pastillas, de modo que el mismo filtro se manejaba de dos formas distintas
// segun la pestaña. Las opciones pueden ser cadenas o {value,label,avatar,color}.
export default function MultiSelect({
  label, options = [], selected = [], onChange,
  clearLabel = "Quitar el filtro", icono = null, minWidth = 170,
  buscarPlaceholder = "Buscar...", sinCoincidencias = "Nada coincide",
  // Reordenar NO es el comportamiento por defecto a proposito: Estado,
  // Prioridad y Columnas llegan en un orden que significa algo (el ciclo de
  // vida del proyecto, la escala de prioridad, las columnas de la tabla) y
  // alfabetizarlo seria estropearlo. Se pide donde la lista es un censo de
  // gente, que es donde el orden de llegada no dice nada.
  ordenar = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(open, ref, panelRef, { altoMax: 320 });
  const inputRef = useRef(null);

  useEffect(() => {
    function fuera(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  // La busqueda no sobrevive al cierre: al reabrir se ve la lista entera, que es
  // lo que se espera de un filtro que se acaba de cerrar.
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQ("");
  }, [open]);

  const norm = useMemo(
    () => options.map(o => (typeof o === "object" ? o : { value: o, label: o })),
    [options]);
  const esta = (v) => selected.some(s => String(s) === String(v));
  const alternar = (v) => onChange(esta(v)
    ? selected.filter(s => String(s) !== String(v))
    : [...selected, String(v)]);

  const activo = selected.length > 0;
  const conBuscador = norm.length >= MIN_BUSCADOR;

  // Con `ordenar`, lo marcado sube arriba y el resto va alfabetico. Antes
  // mandaba el orden en que llegaba la lista —el de la API, que no es ninguno—
  // y desmarcar a alguien obligaba a volver a buscarlo entre sesenta nombres.
  // localeCompare en es para que Álvaro caiga con las aes y no al final.
  const listadas = useMemo(() => {
    const t = norml(q).trim();
    const filtradas = t ? norm.filter(o => norml(o.label).includes(t)) : norm;
    if (!ordenar) return filtradas;
    return [...filtradas].sort((a, b) => {
      const sa = esta(a.value), sb = esta(b.value);
      if (sa !== sb) return sa ? -1 : 1;
      return String(a.label).localeCompare(String(b.label), "es");
    });
  }, [norm, q, selected, ordenar]);

  // Cuantas de las marcadas se ha llevado por delante la busqueda. Sin esto el
  // contador del boton dice 3 y en la lista se ve 1, y parece un fallo.
  const ocultasMarcadas = selected.length - listadas.filter(o => esta(o.value)).length;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="btn" onClick={() => setOpen(o => !o)} title={label}
        style={{
          display: "flex", alignItems: "center", gap: 5, padding: "5px 10px",
          borderRadius: 8, cursor: "pointer", fontFamily: "var(--font)",
          border: "1px solid " + (activo ? "var(--accent)" : "var(--border-ctl)"),
          background: activo ? "var(--accent-bg)" : "var(--surface)",
          color: activo ? "var(--accent)" : "var(--text2)",
          fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
        }}>
        {icono && <Icon name={icono} size={11} color={activo ? "var(--accent)" : "var(--text3)"} />}
        {label}
        {activo && (
          <span style={{ background: "var(--accent)", color: "var(--on-accent)", borderRadius: "50%",
            width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center",
            justifyContent: "center", fontWeight: 800 }}>{selected.length}</span>
        )}
        <Icon name={open ? "chevron-up" : "chevron-down"} size={8} color="var(--text3)" />
      </button>

      {open && (
        <div ref={panelRef} style={{ zIndex: 300,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--shadow-sm)", minWidth, width: "max-content", maxWidth: 300,
          display: "flex", flexDirection: "column", ...posicion }}>

          {/* El buscador va FUERA del area que se desplaza: si se fuera con el
              scroll habria que subir hasta arriba para corregir lo escrito. */}
          {conBuscador && (
            <div style={{ padding: 6, borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <Icon name="search" size={11} color="var(--text3)"
                  style={{ position: "absolute", left: 8, pointerEvents: "none" }} />
                <input aria-label={buscarPlaceholder || "Buscar"} ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                  placeholder={buscarPlaceholder}
                  onKeyDown={e => {
                    if (e.key === "Escape") { e.stopPropagation(); q ? setQ("") : setOpen(false); }
                    // Enter marca la unica que queda: con el nombre escrito ya no
                    // hace falta soltar el teclado para alcanzar la casilla.
                    if (e.key === "Enter" && listadas.length === 1) { alternar(listadas[0].value); setQ(""); }
                  }}
                  style={{ width: "100%", padding: "5px 8px 5px 24px", borderRadius: 7,
                    border: "1px solid var(--border-ctl)", background: "var(--bg)",
                    fontSize: 12, fontFamily: "var(--font)", color: "var(--text)" }} />
              </div>
            </div>
          )}

          <div style={{ overflowY: "auto", padding: "4px 0", flex: 1 }}>
            {norm.length === 0 && (
              <div style={{ padding: "8px 14px", fontSize: 12, color: "var(--text3)" }}>Sin opciones</div>
            )}
            {norm.length > 0 && listadas.length === 0 && (
              <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text3)" }}>
                {sinCoincidencias} con «{q}»
              </div>
            )}
            {listadas.map(o => (
              <label key={o.value} title={o.label}
                style={{ display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 14px", cursor: "pointer", fontSize: 12.5, color: "var(--text)",
                  fontWeight: esta(o.value) ? 600 : 400,
                  background: esta(o.value) ? "var(--accent-bg)" : "transparent" }}>
                <input type="checkbox" checked={esta(o.value)} onChange={() => alternar(o.value)}
                  style={{ accentColor: "var(--accent)", width: 13, height: 13, flexShrink: 0 }} />
                {o.avatar && <Avatar name={o.label} size={20} color={o.color || undefined} />}
                {/* Una linea y puntos suspensivos: con el nombre partido en dos
                    las filas cambian de alto y la lista da saltos al filtrar. */}
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap" }}>{o.label}</span>
              </label>
            ))}
          </div>

          {activo && (
            <div style={{ borderTop: "1px solid var(--border)", flexShrink: 0,
              display: "flex", alignItems: "center", gap: 6, padding: "0 14px" }}>
              <button onClick={() => { onChange([]); setOpen(false); }}
                style={{ padding: "6px 0", border: "none", background: "transparent",
                  fontSize: 11.5, color: "var(--red-text)", cursor: "pointer", textAlign: "left",
                  fontFamily: "var(--font)", fontWeight: 600 }}>{clearLabel}</button>
              {ocultasMarcadas > 0 && (
                <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--text3)" }}
                  title="Siguen aplicandose aunque la busqueda no los enseñe">
                  +{ocultasMarcadas} fuera de la busqueda
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
